import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { adminAuthService } from '../services/adminAuth';
import '../styles/PasswordReset.css';

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('No reset token provided');
      setValidating(false);
      return;
    }

    const result = await adminAuthService.validateResetToken(token);

    if (result.success) {
      setTokenValid(true);
      setEmail(result.email);
    } else {
      setError(result.error || 'Invalid or expired reset token');
    }

    setValidating(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    const result = await adminAuthService.resetPassword(token, newPassword);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/login');
      }, 2000);
    } else {
      setError(result.error || 'Failed to reset password');
    }

    setLoading(false);
  };

  if (validating) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-box">
          <p>Validating reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-box">
          <h1>Reset Link Invalid</h1>
          <p className="error-text">{error}</p>
          <button onClick={() => navigate('/admin/login')} className="back-button">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="password-reset-container">
        <div className="password-reset-box success-box">
          <h1>Password Reset Successful</h1>
          <p>Your password has been reset successfully.</p>
          <p>Redirecting to login page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="password-reset-container">
      <div className="password-reset-box">
        <h1>Set New Password</h1>
        <p className="reset-email">Resetting password for: <strong>{email}</strong></p>

        <form onSubmit={handleSubmit} className="reset-form">
          <div className="form-group">
            <label htmlFor="new-password">New Password</label>
            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading}
              required
              minLength="8"
            />
            <small>Password must be at least 8 characters long</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password">Confirm Password</label>
            <input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading}
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" disabled={loading} className="reset-submit-button">
            {loading ? 'Resetting Password...' : 'Reset Password'}
          </button>
        </form>

        <p className="reset-footer">
          <a href="/admin/login">Back to Login</a>
        </p>
      </div>
    </div>
  );
};

export default PasswordReset;
